/**
 * @fileoverview OnboardingContext - Контекст для управления модальным окном онбординга
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useUserMetadata } from '@/hooks/useUserMetadata';
import { supabaseWorker } from '@/integrations/supabase/workerClient';

interface OnboardingContextType {
  isOpen: boolean;
  handleClose: () => void;
  handleOpen: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const OnboardingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { userMetadata } = useUserMetadata();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  // Проверка: нужно ли показать модальное окно
  useEffect(() => {
    const shouldShow = userMetadata && 
      (userMetadata.onboardingCompleted === false || 
       userMetadata.onboardingCompleted === undefined || 
       userMetadata.onboardingCompleted === null);

    if (shouldShow) {
      // Небольшая задержка для корректной инициализации
      setTimeout(() => {
        setIsOpen(true);
      }, 1000);
    }
  }, [userMetadata]);

  /**
   * Закрыть модальное окно и пометить онбординг как завершенный
   */
  const handleClose = useCallback(async () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[OnboardingContext] Closing onboarding modal');
    }
    setIsOpen(false);

    // КРИТИЧНО: Навигация ПЕРВОЙ — независимо от результата DB операций
    // Это гарантирует что пользователь попадёт в настройки даже если DB недоступна
    if (process.env.NODE_ENV === 'development') {
      console.log('[OnboardingContext] Navigating to settings FIRST...');
    }
    navigate('/settings?tab=system');

    // Очищаем кэш useUserMetadata
    if (typeof window !== 'undefined') {
      // @ts-ignore - очищаем глобальный кэш useUserMetadata
      window.__userMetadataCache = null;
    }

    if (!user?.id) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[OnboardingContext] No user ID, cannot save to DB');
      }
      return;
    }

    // Async DB операции выполняются ПОСЛЕ навигации (fire-and-forget)
    try {
      const { data: rpcResult, error: rpcError } = await supabaseWorker
        .rpc('validate_and_complete_onboarding', { user_uuid: user.id });

      if (rpcError) {
        // Fallback: прямое обновление если RPC недоступен
        await supabaseWorker
          .from('profiles')
          .update({
            onboarding_completed: true,
            onboarding_completed_at: new Date().toISOString(),
            onboarding_step: null,
            updated_at: new Date().toISOString(),
          })
          .eq('user_id', user.id);
      } else if (process.env.NODE_ENV === 'development') {
        const result = rpcResult as { success: boolean; missing_entities?: string[]; error?: string };
        if (!result.success) {
          console.warn('[OnboardingContext] Validation result:', result);
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[OnboardingContext] DB error (non-blocking):', error);
      }
    }
  }, [user?.id, navigate]);

  /**
   * Открыть модальное окно вручную (через кнопку "Инструкция")
   */
  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  return (
    <OnboardingContext.Provider value={{ isOpen, handleClose, handleOpen }}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboardingContext = () => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboardingContext must be used within OnboardingProvider');
  }
  return context;
};
